import React, { useState, useEffect } from 'react';
import Modal from '../../ui/Modal';

// ============================================
// CREATE SLOT MODAL
// ============================================
const CreateSlotModal = ({ isOpen, onClose, onSubmit, initialDate, editingSlot }) => {
  const [formData, setFormData] = useState({
    date: initialDate || '',
    start_time: '',
    end_time: '',
    duration: 60,
    max_clients: 6,
    recurring_weeks: 0
  });

  // Minutes between two HH:MM strings; falls back to 60 when invalid.
  const computeDuration = (start, end) => {
    if (!start || !end) return 60;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const diff = (eh * 60 + em) - (sh * 60 + sm);
    return diff > 0 ? diff : 60;
  };

  // start + duration → HH:MM end string.
  const computeEnd = (start, duration) => {
    if (!start || !duration) return '';
    const [hours, mins] = start.split(':').map(Number);
    const endMins = hours * 60 + mins + parseInt(duration, 10);
    const endHours = Math.floor(endMins / 60) % 24;
    const endMinutes = endMins % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (editingSlot) {
      const dur = computeDuration(editingSlot.start_time, editingSlot.end_time);
      setFormData({
        date: editingSlot.date,
        start_time: editingSlot.start_time,
        end_time: editingSlot.end_time,
        duration: dur,
        max_clients: editingSlot.max_clients || 6,
        recurring_weeks: 0
      });
    } else if (initialDate) {
      setFormData(prev => ({ ...prev, date: initialDate }));
    }
  }, [editingSlot, initialDate]);

  // Single source of truth for end_time: recompute whenever start or duration
  // changes. Fixes the bug where editing only the start hour left a stale or
  // empty end time (e.g. "09:00 – 09:00" / "11:00 –").
  useEffect(() => {
    if (formData.start_time && formData.duration) {
      const newEnd = computeEnd(formData.start_time, formData.duration);
      setFormData(prev => (prev.end_time === newEnd ? prev : { ...prev, end_time: newEnd }));
    }
  }, [formData.start_time, formData.duration]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Strip the helper-only `duration` field from the payload.
    const { duration, ...payload } = formData;
    onSubmit(payload, editingSlot?.id);
  };

  // Standard preset durations; the current slot's duration is injected if it
  // isn't one of the presets so the controlled <select> always reflects state.
  const DURATION_PRESETS = [30, 45, 60, 90, 120];
  const durationOptions = DURATION_PRESETS.includes(Number(formData.duration))
    ? DURATION_PRESETS
    : [...DURATION_PRESETS, Number(formData.duration)].sort((a, b) => a - b);
  const durationLabel = (d) => {
    if (d === 60) return '1 hour';
    if (d === 90) return '1.5 hours';
    if (d === 120) return '2 hours';
    return `${d} min`;
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose} maxWidth="max-w-md">
      <Modal.Header onClose={onClose}>
        <h3 className="text-xl font-bold text-white">
          {editingSlot ? 'Edit Slot' : 'Create Slot'}
        </h3>
      </Modal.Header>

      <Modal.Body>
        <form id="slot-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="input-field w-full"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Start time</label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="input-field w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Duration</label>
              <select
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value, 10) })}
                className="input-field w-full"
              >
                {durationOptions.map((d) => (
                  <option key={d} value={d}>{durationLabel(d)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              End time: <span className="text-blue-300">{formData.end_time || '--:--'}</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Max capacity</label>
            <input
              type="number"
              value={formData.max_clients}
              onChange={(e) => setFormData({ ...formData, max_clients: parseInt(e.target.value) })}
              className="input-field w-full"
              min="1"
              max="20"
              required
            />
          </div>

          {!editingSlot && (
            <div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={formData.recurring_weeks > 0}
                  onChange={(e) => setFormData({ ...formData, recurring_weeks: e.target.checked ? 4 : 0 })}
                  className="w-4 h-4 rounded"
                />
                Repeat weekly
              </label>
              {formData.recurring_weeks > 0 && (
                <select
                  value={formData.recurring_weeks}
                  onChange={(e) => setFormData({ ...formData, recurring_weeks: parseInt(e.target.value) })}
                  className="input-field w-full mt-2"
                >
                  <option value={4}>4 weeks</option>
                  <option value={8}>8 weeks</option>
                  <option value={12}>12 weeks</option>
                  <option value={24}>24 weeks</option>
                </select>
              )}
            </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer>
        <button type="button" onClick={onClose} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" form="slot-form" className="btn-primary">
          {editingSlot ? 'Save changes' : 'Create slot'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateSlotModal;
